import React, { useState } from "react";
import "../Assets/css/login.css"; // reuse same styles
import axiosUserInstance from "../axiosUserInstance";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { Button } from "react-bootstrap";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import BgVideo from "../Assets/video/login-signup-bg.mp4";
import PhoneVerificationModal from "./Signup/PhoneVerificationModal";

const Signup = () => {
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [muted, setMuted] = useState(true);

  // Referral state
  const [referralCode, setReferralCode] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [referralSuccess, setReferralSuccess] = useState("");

  const navigate = useNavigate();

  // 🔹 Validate email with regex
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // 🔹 Validate userName (url friendly: letters, numbers, underscore, hyphen)
  const validateUsername = (userName) => {
    const regex = /^[a-zA-Z0-9_-]{3,20}$/;
    return regex.test(userName);
  };

  const handleReferralFieldFocus = () => {
    if (!phoneVerified) {
      setShowPhoneModal(true);
    }
  };

  const handlePhoneVerified = (phone) => {
    setVerifiedPhone(phone);
    setPhoneVerified(true);
    setShowPhoneModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset errors
    setUsernameError("");
    setEmailError("");
    setPasswordError("");

    let valid = true;

    // Username validation
    if (!userName) {
      setUsernameError("Username is required.");
      valid = false;
    } else if (!validateUsername(userName)) {
      setUsernameError(
        "Username must be 3–20 characters, letters/numbers/underscores only."
      );
      valid = false;
    }

    // Email validation
    if (!email) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email.");
      valid = false;
    }

    // Password validation
    if (!password) {
      setPasswordError("Password is required.");
      valid = false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      valid = false;
    }

    if (!valid) return; // Stop if validation fails

    setIsLoading(true);

    try {
      const response = await axiosUserInstance.post(`/user/signUp`, {
        userName,
        email,
        password,
      });

      if (response?.status === 201 || response?.status === 200) {
        // If a referral code was entered and phone was verified, redeem it
        if (referralCode.trim() && phoneVerified && response.data?.token) {
          try {
            const redeemRes = await axiosUserInstance.post(
              `/referral/redeem`,
              { referralCode: referralCode.trim(), phone: verifiedPhone },
              { headers: { Authorization: `Bearer ${response.data.token}` } }
            );
            if (redeemRes.status === 200) {
              setReferralSuccess(redeemRes.data.message);
              // Brief pause so the user can read the message, then navigate
              setTimeout(() => navigate("/auth/login"), 4000);
              return;
            }
          } catch (redeemErr) {
            // Referral redeem failed - still register succeeded
            toast.warning(
              redeemErr?.response?.data?.message ||
                "Signup successful but referral code could not be applied."
            );
          }
        }

        toast.success("Signup successful. Please log in.");
        navigate("/auth/login");
      } else {
        toast.error(
          response?.data?.message || "Signup failed. Please try again."
        );
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred. Please try again.";
      toast.error(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setIsLoading(true);
      const response = await axiosUserInstance.post(`/user/signUp`, {
        mode: "googleSignup",
        googleToken: credentialResponse.credential,
      });

      if (response?.status === 200 || response?.status === 201) {
        const data = await response.data;
        localStorage.setItem("token", data.token);
        const decoded = jwtDecode(data.token);
        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            userId: decoded?.userId,
            userName: decoded?.userName,
            email: decoded?.email,
          })
        );
        if (decoded?.roleId === "user") {
          navigate("/user/dashboard");
        } else if (decoded?.roleId === "admin") {
          navigate("/admin/dashboard");
        }
        toast.success("Signup successful. Welcome to BioME!");
      } else {
        toast.error(response?.message || "Signup failed. Please try again.");
      }
    } catch (err) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        "Google signup failed. Please try again.";
      toast.error(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error("Google signup failed. Please try again.");
  };

  return (
    <div className="login-page">
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted={muted}
        playsInline
        className="video-background"
        id="bgVideo"
      >
        <source src={BgVideo} type="video/mp4" />
      </video>

      {/* Toggle Button */}
      <button className="audio-toggle" onClick={() => setMuted(!muted)}>
        <i className={`fas ${muted ? "fa-volume-mute" : "fa-volume-up"}`}></i>
      </button>

      {/* Phone Verification Modal */}
      {showPhoneModal && (
        <PhoneVerificationModal
          onVerified={handlePhoneVerified}
          onClose={() => setShowPhoneModal(false)}
        />
      )}

      {/* Referral Success Banner */}
      {referralSuccess && (
        <div className="referral-success-banner">
          <div className="referral-success-inner">
            <span className="referral-success-icon">🎉</span>
            <p>{referralSuccess}</p>
            <small>Redirecting you to login…</small>
          </div>
        </div>
      )}

      {/* Signup Box */}
      <div className="login-container">
        <div className="login-header">
          <h1>Sign Up</h1>
          <p>Create your BioME.com account</p>
        </div>

        <div className="login-form">
          <form onSubmit={handleSubmit}>
            {/* Username */}
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                name="username"
                className="form-control"
                placeholder="Enter a username"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
              {usernameError && (
                <div className="error-message">{usernameError}</div>
              )}
            </div>

            {/* Email */}
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailError && <div className="error-message">{emailError}</div>}
            </div>

            {/* Password */}
            <div className="form-group">
              <label>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="form-control"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "5px",
                    display: "flex",
                    alignItems: "center",
                    color: "#666",
                  }}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <AiOutlineEyeInvisible size={20} />
                  ) : (
                    <AiOutlineEye size={20} />
                  )}
                </button>
              </div>
              {passwordError && (
                <div className="error-message">{passwordError}</div>
              )}
            </div>

            {/* Referral Code */}
            <div className="form-group referral-group">
              <label>
                Referral Code
                <span className="referral-optional"> (Optional)</span>
              </label>
              <div className="referral-input-wrapper">
                <input
                  type="text"
                  id="referral-code-input"
                  className={`form-control referral-input ${phoneVerified ? "referral-unlocked" : "referral-locked"}`}
                  placeholder={
                    phoneVerified
                      ? "Enter referral code (e.g. IWBIOME4FR)"
                      : "🔒 Verify phone to unlock"
                  }
                  value={referralCode}
                  onChange={(e) =>
                    setReferralCode(
                      e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")
                    )
                  }
                  onFocus={handleReferralFieldFocus}
                  onClick={handleReferralFieldFocus}
                  readOnly={!phoneVerified}
                  maxLength={20}
                  autoComplete="off"
                />
                {phoneVerified && (
                  <span
                    className="referral-verified-badge"
                    title="Phone verified"
                  >
                    ✓ Phone verified
                  </span>
                )}
              </div>
            </div>

            <Button
              className="btn btn-primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Loading…" : "Sign Up"}
            </Button>

            <div className="divider">or</div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "15px",
              }}
            >
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
                logo_alignment="left"
              />
            </div>

            <div className="login-footer">
              Already have an account? <a href="/auth/login">Login</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
